/**
 * useSchemaTypeCacheReset.ts
 *
 * Clears the schemaTypes demand cache and the request coalescer whenever the
 * active organization changes — a switch of the tab's active organization
 * (`x-center-organization-id`) or a cross-user impersonation start/end.
 *
 * The cache key embeds organizationId, so stale entries are never *served*
 * across orgs. But without this reset they accumulate unbounded for every org
 * visited in a session, and a batch registered just before a switch would
 * flush into the previous org's context. Spec: "Clear `byKey` on org change to
 * bound memory."
 */
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useActiveOrganization, useImpersonation } from '@center-technologies/shared-api-clients';
import type { AppDispatch } from '@center-pwa/store/store';
import { clearAll } from '@center-pwa/reducers/schemaTypesReducer';
import { resetCoalescer } from './requestCoalescer';
import { resetViewPersonalizationCache } from './useViewPersonalization';
import { resetContextViewPermissionsCache } from './useContextViewPermissions';
import { resetContextPermissionsCache } from './useContextPermissions';
import { resetPlatformAdminCache } from './usePlatformAdmin';
import { resetOrgAdminCache } from './useOrgAdmin';
import { resetMarketplacePermissionsCache } from './useMarketplacePermissions';
import { resetSpherePermissionCache } from './spherePermissionCache';
import { resetSphereTypeCache } from './useSphereType';
import { resetAllViewStateSessions } from '../context/ViewStateContext';

export function useSchemaTypeCacheReset(): void {
  const dispatch = useDispatch<AppDispatch>();
  const { impersonationDetails } = useImpersonation();
  const { activeOrganizationId } = useActiveOrganization();
  // The tab's active organization is the org for a plain switch; impersonation
  // still carries its own organizationId for the cross-user case. Either one
  // changing is a genuine org-to-org switch.
  const organizationId = impersonationDetails?.organizationId ?? activeOrganizationId ?? undefined;
  const sessionId = impersonationDetails?.sessionId;
  const prevOrgRef = useRef<string | undefined>(organizationId);
  // `null` is the session's own scope (no impersonation); `undefined` is "not yet known".
  const prevSessionRef = useRef<string | null | undefined>(sessionId);

  useEffect(() => {
    // Only reset on a genuine org-to-org switch, not the initial
    // undefined -> first-org population.
    const orgSwitched =
      prevOrgRef.current !== undefined && prevOrgRef.current !== organizationId;
    // The impersonation session, unlike the org, IS meaningful on its
    // undefined -> value edge: `impersonationDetails` is null until
    // impersonation starts, so that transition is precisely the moment the
    // acting identity changes. Excluding it (as the org guard must) would mean
    // no reset fires when impersonation BEGINS — only when it ends.
    const identitySwitched = prevSessionRef.current !== sessionId;

    if (orgSwitched || identitySwitched) {
      resetCoalescer();
      dispatch(clearAll());
      // The org/impersonation-switch signal is the one real "logout"-shaped
      // event this app fires — see specs/view-personalization-overlay.md's
      // UI Components table. Piggybacking here (rather than a new call site)
      // avoids a second untested hook nobody actually invokes.
      //
      // Defence in depth only: `useViewPersonalization` keys its cache by the
      // acting identity, so a leak does not depend on this hook being mounted
      // or on effect ordering. This bounds memory across identity switches.
      resetViewPersonalizationCache();
      // NOT defence in depth — load-bearing. A context-view permission
      // decision lives in each mount's state, and `ApiClientsProvider`
      // deliberately does not rebuild its clients on impersonation (the bearer
      // token is resolved per-request through `tokenProvider`), so
      // `useContextViewPermissions`' `[apiClients, viewSystemName]` deps do
      // not change across a switch. Without this the acting identity inherits
      // the previous identity's `canEdit`. EAINT-789 D-4.
      resetContextViewPermissionsCache();
      // The CONTEXT-level twin, for exactly the same reason. It was omitted
      // when D-4 was wired because `resetContextPermissionsCache` was believed
      // to be a pure coalescer clear; it is not — `useContextPermissions` holds
      // its decision in state too, and it feeds `canEditContext` in
      // `TreeMapOrganism` whenever no view is active. Found in review pass 4:
      // the function had zero production call sites, so the context-level
      // decision was never invalidated on an identity switch at all.
      resetContextPermissionsCache();
      // Load-bearing, and the worst instance of this shape so far. Both caches
      // hold the RESOLVED answer at module scope keyed on the ApiClient
      // reference — the reference that deliberately does not change across an
      // impersonation switch. So a platform admin who starts impersonating
      // carries `isPlatformAdmin: true` into the target's session. Strictly
      // worse than the context-view twin: that one held an in-flight promise
      // and emptied on settle, bounding exposure to a request window; these
      // hold a result for the module's lifetime. Both reset functions were
      // documented "for tests and logout flows" and had zero runtime callers.
      // EAINT-798.
      resetPlatformAdminCache();
      resetOrgAdminCache();
      // The same shape a third time, found while wiring the two above and not
      // named in EAINT-798: publisher permissions are cached in a WeakMap keyed
      // on the `schemaClient` reference, with an exported reset that likewise
      // had no runtime caller.
      resetMarketplacePermissionsCache();
      // Defence in depth only — `spherePermissionCache` keys by
      // `identityId::sphereId::action`, so a second identity can never read the
      // first's entry and there is no leak to close. Wiring it bounds growth
      // across switches and honours the docstring on
      // `resetSpherePermissionCache` ("Call on logout and on impersonation
      // start/end"), which had no caller. Covers `resetPropertyAccessCache`,
      // which delegates to it. EAINT-790.
      resetSpherePermissionCache();
      // Load-bearing, same shape as the two above. `useSphereType` caches
      // ancestor schema reads at module scope keyed on the `ApiClient`
      // reference — the reference that deliberately does not change across an
      // impersonation switch. Ancestors are not necessarily platform schemas:
      // an org schema may extend another org schema, which `GET /schemas/:id`
      // gates behind a SpiceDB `view` check. Without this reset the acting
      // identity inherits the previous identity's cached ancestor names and
      // sees `truncated: false` where an honest walk for the new identity
      // would truncate — i.e. the lineage breadcrumb stops being
      // permission-honest, which is the one property it claims. EAINT-892.
      resetSphereTypeCache();
      // The session layer is the other piece of per-mount state D-4's
      // reasoning applies to: before `ViewStateProvider` was hoisted above the
      // router (EAINT-806 Phase 1), the TreeMap unmounting on navigation
      // cleared it implicitly. Hoisting the provider gave the session layer a
      // lifetime that now outlives an identity switch, so without this call
      // the previous identity's unsaved filters compose into the new
      // identity's `runtimeFilters` — not a leak (the server still re-filters
      // per identity), but a silent narrowing of the acting identity's rows.
      // EAINT-806 Phase 2.
      resetAllViewStateSessions();
    }
    prevOrgRef.current = organizationId;
    prevSessionRef.current = sessionId;
  }, [organizationId, sessionId, dispatch]);
}
